import { MouseEvent, ReactElement, useEffect, useRef, useState } from 'react';

import { IconButton, IconButtonProps, Spinner, Tooltip } from '@chakra-ui/react';
import { IconBaseProps } from 'react-icons';
import { BiCopyAlt } from 'react-icons/bi';

import { formatNumber } from '../../libs/utils';

type Props = Omit<IconButtonProps, 'aria-label'> & {
  value?: string;
  getValueAsync?: () => Promise<string>;
  isFormatted?: boolean;
  iconProps?: IconBaseProps;
  icon?: ReactElement;
  forceUppercase?: boolean;
};

const CopyButtonV2 = ({
  value,
  getValueAsync,
  isFormatted = false,
  iconProps,
  icon,
  forceUppercase = false,
  ...props
}: Props) => {
  const [show, setShow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClick = async (e: MouseEvent) => {
    e.stopPropagation();
    setIsLoading(true);
    setHasError(false);
    let textToCopy = value || '';

    try {
      if (getValueAsync) {
        textToCopy = await getValueAsync();
      }

      if (isFormatted) {
        await navigator.clipboard.writeText(formatNumber(textToCopy));
      } else if (forceUppercase) {
        await navigator.clipboard.writeText(textToCopy.toUpperCase());
      } else {
        await navigator.clipboard.writeText(textToCopy);
      }
    } catch (error) {
      setHasError(true);
    }

    setShow(true);
    setIsLoading(false);
    timeoutRef.current = setTimeout(() => {
      setShow(false);
      setHasError(false);
    }, 2750);
  };

  const buttonIcon = icon || <BiCopyAlt {...iconProps} />;

  return (
    <Tooltip
      isOpen={show}
      hasArrow
      label={hasError ? 'Failed to copy' : 'Copied!'}
      bg={hasError ? 'red.500' : 'gray.700'}
      color="white"
    >
      <IconButton
        icon={isLoading ? <Spinner size="sm" /> : buttonIcon}
        variant="iconHover"
        size="xs"
        fontSize={12}
        onClick={handleClick}
        isDisabled={isLoading}
        {...props}
        aria-label="Copy"
      />
    </Tooltip>
  );
};

export default CopyButtonV2;
